import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuthStore'
import { PageHeader } from '../components/PageHeader'
import { DashboardCard } from '../components/DashboardCard'
import apiService from '../services/api'

// Local formatters
const formatThaiCurrency = (amount: number) => {
  return new Intl.NumberFormat('th-TH', {
    style: 'currency',
    currency: 'THB',
    minimumFractionDigits: 0
  }).format(amount)
}

interface Course {
  id: number
  name: string
  description: string
  price: number
  schedule: string
  subjects: string[]
  max_capacity: number
  is_active: boolean
  capacity: {
    max_capacity: number
    current_enrollments: number
    available_spots: number
    is_full: boolean
  }
}

export const CourseSelectionPage: React.FC = () => {
  const navigate = useNavigate()
  const [courses, setCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadCourses()
  }, [])

  const loadCourses = async () => {
    try {
      setLoading(true)
      const coursesData = await apiService.getCourses(true)
      setCourses(coursesData)
    } catch (err) {
      setError('ไม่สามารถโหลดข้อมูลคอร์สได้')
      console.error('Failed to load courses:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCourseSelect = (course: Course) => {
    // Navigate to enrollment form with course data
    navigate('/enrollment/new', { 
      state: { 
        course,
        from: 'course-selection'
      } 
    })
  }

  const getAvailabilityStatus = (course: Course) => {
    if (course.capacity.is_full) {
      return { text: 'เต็ม', variant: 'error' as const }
    }
    if (course.capacity.available_spots <= 3) {
      return { text: `เหลือ ${course.capacity.available_spots} ที่`, variant: 'warning' as const }
    }
    return { text: `เหลือ ${course.capacity.available_spots} ที่`, variant: 'success' as const }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="เลือกคอร์สเรียน" />
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลดคอร์สเรียน...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="เลือกคอร์สเรียน" />
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={loadCourses}
              className="btn-primary"
            >
              ลองใหม่
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="เลือกคอร์สเรียน" 
        showBackButton
        onBack={() => navigate('/dashboard')}
      />
      
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header Info */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            คอร์สเรียนที่เปิดให้ลงทะเบียน
          </h2>
          <p className="text-gray-600">
            เลือกคอร์สที่เหมาะสมกับลูกของคุณ ทุกคอร์สเรียนเฉพาะวันหยุดเสาร์-อาทิตย์
          </p>
        </div>

        {/* Course Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {courses.map((course) => {
            const availability = getAvailabilityStatus(course)
            
            return (
              <DashboardCard
                key={course.id}
                title={course.name}
                className="transition-all duration-200 hover:shadow-lg hover:-translate-y-1"
                onClick={() => !course.capacity.is_full && handleCourseSelect(course)}
              >
                <div className="space-y-4">
                  {/* Price */}
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {formatThaiCurrency(course.price)}
                    </div>
                    <div className="text-sm text-gray-500">ต่อเดือน</div>
                  </div>

                  {/* Schedule */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">เวลาเรียน</h4>
                    <p className="text-sm text-gray-600">{course.schedule}</p>
                  </div>

                  {/* Subjects */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">วิชาที่เรียน</h4>
                    <div className="flex flex-wrap gap-1">
                      {course.subjects.map((subject, index) => (
                        <span
                          key={index}
                          className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                        >
                          {subject}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {course.description}
                    </p>
                  </div>

                  {/* Availability */}
                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">ความว่าง:</span>
                      <span className={`text-sm font-medium ${
                        availability.variant === 'error' ? 'text-red-600' :
                        availability.variant === 'warning' ? 'text-yellow-600' :
                        'text-green-600'
                      }`}>
                        {availability.text}
                      </span>
                    </div>
                    <div className="mt-1">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            availability.variant === 'error' ? 'bg-red-500' :
                            availability.variant === 'warning' ? 'bg-yellow-500' :
                            'bg-green-500'
                          }`}
                          style={{ 
                            width: `${(course.capacity.current_enrollments / course.capacity.max_capacity) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {course.capacity.current_enrollments}/{course.capacity.max_capacity} คน
                      </div>
                    </div>
                  </div>

                  {/* Action Button */}
                  <button
                    onClick={() => handleCourseSelect(course)}
                    disabled={course.capacity.is_full}
                    className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                      course.capacity.is_full
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {course.capacity.is_full ? 'คอร์สเต็ม' : 'เลือกคอร์สนี้'}
                  </button>
                </div>
              </DashboardCard>
            )
          })}
        </div>

        {/* Empty State */}
        {courses.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">ไม่มีคอร์สที่เปิดให้ลงทะเบียน</h3>
            <p className="text-gray-600">กรุณาติดต่อเจ้าหน้าที่สำหรับข้อมูลเพิ่มเติม</p>
          </div>
        )}

        {/* Additional Info */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-medium text-blue-900 mb-2">ข้อมูลสำคัญ</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• การเรียนทุกคอร์สจะเป็นการเรียนเฉพาะวันหยุดเสาร์-อาทิตย์</li>
            <li>• หลังจากเลือกคอร์สและกรอกข้อมูล จะต้องชำระเงินเพื่อยืนยันการลงทะเบียน</li>
            <li>• สามารถต่ออายุคอร์สเรียนเป็นรายเดือนได้</li>
            <li>• หากต้องการเปลี่ยนคอร์ส กรุณาติดต่อเจ้าหน้าที่</li>
          </ul>
        </div>
      </div>
    </div>
  )
}